import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { apiGet, apiPut } from '../../services/api'
import { useAuthStore } from '../../store/authStore'
import {
  Settings,
  Globe,
  DollarSign,
  Calendar,
  Clock,
  Save,
  Check,
  AlertCircle,
  Users,
  RefreshCw,
  ExternalLink,
  Shield,
} from 'lucide-react'
import LoadingSpinner from '../../components/common/LoadingSpinner'
import { getEagleVisionStatus, getSyncStatus, SyncLog } from '../../services/eagleVision'
import RoleMappingsModal from '../../components/admin/RoleMappingsModal'

interface CompanySettings {
  id: number
  name: string
  language: string
  currency: string
  date_format: string
  timezone: string
}

const LANGUAGES = [
  { code: 'en', name: 'English' },
  { code: 'id', name: 'Bahasa Indonesia' },
  { code: 'ms', name: 'Bahasa Melayu' },
  { code: 'zh', name: '中文 (Chinese)' },
  { code: 'ja', name: '日本語 (Japanese)' },
  { code: 'ko', name: '한국어 (Korean)' },
  { code: 'th', name: 'ไทย (Thai)' },
  { code: 'vi', name: 'Tiếng Việt (Vietnamese)' },
]

const CURRENCIES = [
  { code: 'USD', name: 'US Dollar', symbol: '$' },
  { code: 'IDR', name: 'Indonesian Rupiah', symbol: 'Rp' },
  { code: 'MYR', name: 'Malaysian Ringgit', symbol: 'RM' },
  { code: 'SGD', name: 'Singapore Dollar', symbol: 'S$' },
  { code: 'JPY', name: 'Japanese Yen', symbol: '¥' },
  { code: 'CNY', name: 'Chinese Yuan', symbol: '¥' },
  { code: 'KRW', name: 'Korean Won', symbol: '₩' },
  { code: 'THB', name: 'Thai Baht', symbol: '฿' },
  { code: 'VND', name: 'Vietnamese Dong', symbol: '₫' },
  { code: 'PHP', name: 'Philippine Peso', symbol: '₱' },
  { code: 'EUR', name: 'Euro', symbol: '€' },
  { code: 'GBP', name: 'British Pound', symbol: '£' },
  { code: 'AUD', name: 'Australian Dollar', symbol: 'A$' },
]

const DATE_FORMATS = [
  { code: 'DD/MM/YYYY', name: 'DD/MM/YYYY', example: '18/12/2025' },
  { code: 'MM/DD/YYYY', name: 'MM/DD/YYYY', example: '12/18/2025' },
  { code: 'YYYY-MM-DD', name: 'YYYY-MM-DD', example: '2025-12-18' },
  { code: 'DD-MM-YYYY', name: 'DD-MM-YYYY', example: '18-12-2025' },
  { code: 'DD MMM YYYY', name: 'DD MMM YYYY', example: '18 Dec 2025' },
  { code: 'MMM DD, YYYY', name: 'MMM DD, YYYY', example: 'Dec 18, 2025' },
]

const TIMEZONES = [
  { code: 'Asia/Jakarta', name: 'Jakarta (WIB)', offset: 'UTC+7' },
  { code: 'Asia/Makassar', name: 'Makassar (WITA)', offset: 'UTC+8' },
  { code: 'Asia/Jayapura', name: 'Jayapura (WIT)', offset: 'UTC+9' },
  { code: 'Asia/Singapore', name: 'Singapore', offset: 'UTC+8' },
  { code: 'Asia/Kuala_Lumpur', name: 'Kuala Lumpur', offset: 'UTC+8' },
  { code: 'Asia/Tokyo', name: 'Tokyo', offset: 'UTC+9' },
  { code: 'Asia/Seoul', name: 'Seoul', offset: 'UTC+9' },
  { code: 'Asia/Shanghai', name: 'Shanghai', offset: 'UTC+8' },
  { code: 'Asia/Hong_Kong', name: 'Hong Kong', offset: 'UTC+8' },
  { code: 'Asia/Bangkok', name: 'Bangkok', offset: 'UTC+7' },
  { code: 'Asia/Ho_Chi_Minh', name: 'Ho Chi Minh', offset: 'UTC+7' },
  { code: 'Asia/Manila', name: 'Manila', offset: 'UTC+8' },
  { code: 'Australia/Sydney', name: 'Sydney', offset: 'UTC+10/11' },
  { code: 'Europe/London', name: 'London', offset: 'UTC+0/1' },
  { code: 'America/New_York', name: 'New York', offset: 'UTC-5/-4' },
  { code: 'America/Los_Angeles', name: 'Los Angeles', offset: 'UTC-8/-7' },
]

export default function CompanySettingsPage() {
  const { user, isImpersonating, originalUser } = useAuthStore()
  const [, setSettings] = useState<CompanySettings | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  // Form state
  const [language, setLanguage] = useState('en')
  const [currency, setCurrency] = useState('IDR')
  const [dateFormat, setDateFormat] = useState('DD/MM/YYYY')
  const [timezone, setTimezone] = useState('Asia/Jakarta')

  // Eagle Vision state
  const [evEnabled, setEvEnabled] = useState(false)
  const [evLastSync, setEvLastSync] = useState<SyncLog | null>(null)

  // Role Mappings state - only for superadmin
  const [isRoleMappingsModalOpen, setIsRoleMappingsModalOpen] = useState(false)
  const isSuperadmin = isImpersonating && originalUser?.role === 'superadmin'

  useEffect(() => {
    fetchSettings()
    fetchEagleVisionStatus()
  }, [])

  const fetchSettings = async () => {
    try {
      const data = await apiGet<CompanySettings>('/companies/settings')
      setSettings(data)
      setLanguage(data.language || 'en')
      setCurrency(data.currency || 'IDR')
      setDateFormat(data.date_format || 'DD/MM/YYYY')
      setTimezone(data.timezone || 'Asia/Jakarta')
    } catch (err) {
      console.error('Failed to load settings:', err)
    } finally {
      setIsLoading(false)
    }
  }

  const fetchEagleVisionStatus = async () => {
    try {
      const [status, syncStatus] = await Promise.all([
        getEagleVisionStatus(),
        getSyncStatus().catch(() => ({ last_sync: null, logs: [] }))
      ])
      setEvEnabled(status.enabled)
      setEvLastSync(syncStatus.last_sync)
    } catch {
      // Eagle Vision not available
    }
  }

  const handleSave = async () => {
    setIsSaving(true)
    setError(null)
    setSuccess(false)

    try {
      await apiPut('/companies/settings', {
        language,
        currency,
        date_format: dateFormat,
        timezone,
      })
      setSuccess(true)
      setTimeout(() => setSuccess(false), 3000)
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save settings')
    } finally {
      setIsSaving(false)
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Company Settings</h1>
          <p className="text-gray-500 mt-1">
            Configure regional settings for {user?.company?.name || 'your company'}
          </p>
        </div>
      </div>

      {/* Settings Form */}
      <div className="bg-white rounded-xl shadow-sm border">
        <div className="p-6 border-b">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Settings className="h-5 w-5 text-gray-500" />
            Regional Settings
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            These settings will be applied across your company's KPI system
          </p>
        </div>

        <div className="p-6 space-y-6">
          {/* Language */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-start">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Globe className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-900">
                  Language
                </label>
                <p className="text-xs text-gray-500">Display language for the app</p>
              </div>
            </div>
            <div className="md:col-span-2">
              <select
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                className="input w-full"
              >
                {LANGUAGES.map((lang) => (
                  <option key={lang.code} value={lang.code}>
                    {lang.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Currency */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-start border-t pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <DollarSign className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-900">
                  Currency
                </label>
                <p className="text-xs text-gray-500">Default currency for reports</p>
              </div>
            </div>
            <div className="md:col-span-2">
              <select
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
                className="input w-full"
              >
                {CURRENCIES.map((curr) => (
                  <option key={curr.code} value={curr.code}>
                    {curr.symbol} - {curr.name} ({curr.code})
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Date Format */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-start border-t pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Calendar className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-900">
                  Date Format
                </label>
                <p className="text-xs text-gray-500">How dates are displayed</p>
              </div>
            </div>
            <div className="md:col-span-2">
              <select
                value={dateFormat}
                onChange={(e) => setDateFormat(e.target.value)}
                className="input w-full"
              >
                {DATE_FORMATS.map((df) => (
                  <option key={df.code} value={df.code}>
                    {df.name} (e.g., {df.example})
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Timezone */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-start border-t pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <Clock className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-900">
                  Timezone
                </label>
                <p className="text-xs text-gray-500">For scheduling and deadlines</p>
              </div>
            </div>
            <div className="md:col-span-2">
              <select
                value={timezone}
                onChange={(e) => setTimezone(e.target.value)}
                className="input w-full"
              >
                {TIMEZONES.map((tz) => (
                  <option key={tz.code} value={tz.code}>
                    {tz.name} ({tz.offset})
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 bg-gray-50 border-t rounded-b-xl">
          <div className="flex items-center justify-between">
            <div>
              {error && (
                <div className="flex items-center gap-2 text-red-600">
                  <AlertCircle className="h-4 w-4" />
                  <span className="text-sm">{error}</span>
                </div>
              )}
              {success && (
                <div className="flex items-center gap-2 text-green-600">
                  <Check className="h-4 w-4" />
                  <span className="text-sm">Settings saved successfully!</span>
                </div>
              )}
            </div>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="btn-primary flex items-center gap-2"
            >
              {isSaving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  Save Settings
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Preview Card */}
      <div className="bg-white rounded-xl shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Preview</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="p-4 bg-gray-50 rounded-lg">
            <p className="text-xs text-gray-500 mb-1">Language</p>
            <p className="font-medium">
              {LANGUAGES.find(l => l.code === language)?.name || language}
            </p>
          </div>
          <div className="p-4 bg-gray-50 rounded-lg">
            <p className="text-xs text-gray-500 mb-1">Currency</p>
            <p className="font-medium">
              {CURRENCIES.find(c => c.code === currency)?.symbol} 1,000.00
            </p>
          </div>
          <div className="p-4 bg-gray-50 rounded-lg">
            <p className="text-xs text-gray-500 mb-1">Date</p>
            <p className="font-medium">
              {DATE_FORMATS.find(d => d.code === dateFormat)?.example}
            </p>
          </div>
          <div className="p-4 bg-gray-50 rounded-lg">
            <p className="text-xs text-gray-500 mb-1">Timezone</p>
            <p className="font-medium">
              {TIMEZONES.find(t => t.code === timezone)?.offset}
            </p>
          </div>
        </div>
      </div>

      {/* Eagle Vision HR Integration */}
      <div className="bg-white rounded-xl shadow-sm border">
        <div className="p-6 border-b">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Users className="h-5 w-5 text-gray-500" />
            Eagle Vision HR Integration
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Sync employees and attendance from Eagle Vision HR system
          </p>
        </div>

        <div className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className={`p-3 rounded-lg ${evEnabled ? 'bg-green-100' : 'bg-gray-100'}`}>
                <RefreshCw className={`h-6 w-6 ${evEnabled ? 'text-green-600' : 'text-gray-400'}`} />
              </div>
              <div>
                <p className="font-medium text-gray-900">
                  Status: {evEnabled ? 'Connected' : 'Not Configured'}
                </p>
                {evLastSync && (
                  <p className="text-sm text-gray-500">
                    Last sync: {new Date(evLastSync.started_at).toLocaleString()}
                    {' '}({evLastSync.records_synced} records)
                  </p>
                )}
                {!evLastSync && evEnabled && (
                  <p className="text-sm text-gray-500">No sync history yet</p>
                )}
              </div>
            </div>

            {evEnabled && (
              <Link
                to="/admin/eagle-vision"
                className="btn-primary flex items-center gap-2"
              >
                <ExternalLink className="h-4 w-4" />
                Manage Sync
              </Link>
            )}
          </div>

          {!evEnabled && (
            <div className="mt-4 p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600">
                Eagle Vision HR integration is not enabled. Contact your system administrator to configure the integration.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Role Mappings - Superadmin Only */}
      {isSuperadmin && user?.company_id && (
        <>
          <div className="bg-white rounded-xl shadow-sm border">
            <div className="p-6 border-b">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Shield className="h-5 w-5 text-gray-500" />
                Role Mappings
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                Configure how Eagle Vision roles map to AEON KPI roles for this company
              </p>
            </div>

            <div className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-blue-100 rounded-lg">
                    <Shield className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">
                      Eagle Vision Role Mappings
                    </p>
                    <p className="text-sm text-gray-500">
                      Map EV roles and designations to AEON KPI roles
                    </p>
                  </div>
                </div>

                <button
                  onClick={() => setIsRoleMappingsModalOpen(true)}
                  className="btn-primary flex items-center gap-2"
                >
                  <Shield className="h-4 w-4" />
                  Manage Mappings
                </button>
              </div>
            </div>
          </div>

          <RoleMappingsModal
            isOpen={isRoleMappingsModalOpen}
            onClose={() => setIsRoleMappingsModalOpen(false)}
            companyId={user.company_id}
            companyName={user.company?.name || 'Company'}
          />
        </>
      )}
    </div>
  )
}
